import React, { useEffect, useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { SiTether } from "react-icons/si";
import {
  getWallets,
  initiateWalletFunding,
  processWalletFunding,
} from "./apiWallets";
import Preloader from "./components/Preloader";
import { useParams, useNavigate } from "react-router-dom";
import { useWallet } from "./api/hooks/useWallet";

export default function WalletFunding({ setSidebarOpen }) {
  const { sourceWalletId } = useParams();
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [error, setError] = useState("");

  // Funding state
  const [activeStep, setActiveStep] = useState(0);
  const [sourceWallet, setSourceWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("NGN");
  const [description, setDescription] = useState("");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [fundingLoading, setFundingLoading] = useState(false);
  const [fundingError, setFundingError] = useState("");
  const [fundingResult, setFundingResult] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  const steps = ["Enter Amount", "Confirm Funding", "Payment"];
  const supportedCurrencies = ["NGN", "USD", "GBP"];

  const { data: walletsData, isLoading } = useWallet();

  useEffect(() => {
    if (walletsData) {
      setWallets(walletsData.data ?? []);

      if (sourceWalletId) {
        const foundWallet = walletsData.data.find(
          (w) => w.id === sourceWalletId || w.wallet_number === sourceWalletId
        );
        if (foundWallet) {
          setSourceWallet(foundWallet);
        }
      }
    }
  }, [walletsData, sourceWalletId]);

  // Helper functions
  function getFlag(code) {
    if (code === "NGN") return "🇳🇬";
    if (code === "USD") return "🇺🇸";
    if (code === "USDT") return "USDT";
    if (code === "GBP") return "🇬🇧";
    return "💰";
  }

  function getSymbol(code) {
    if (code === "NGN") return "₦";
    if (code === "USD" || code === "USDT") return "$";
    if (code === "GBP") return "£";
    return "";
  }

  function getCurrencyCode(wallet) {
    if (!wallet) return "N/A";

    if (wallet.provider_metadata) {
      try {
        const meta = JSON.parse(wallet.provider_metadata);
        if (meta.currency) return meta.currency;
      } catch {}
    }
    return wallet.currency?.code || wallet.currency?.name || "N/A";
  }

  function getValidWalletNumber(wallet) {
    if (!wallet) return null;
    if (wallet.wallet_number && wallet.wallet_number.length > 0) {
      return wallet.wallet_number;
    }
    return null;
  }

  const handleNext = () => {
    if (activeStep === 0) {
      if (!amount || Number(amount) <= 0) {
        setFundingError("Enter a valid amount");
        return;
      }
      setActiveStep(1);
      setFundingError("");
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setFundingError("");
  };

  const handleFund = async () => {
    setFundingLoading(true);
    setFundingError("");

    // Validate wallet number
    const validWalletNumber = getValidWalletNumber(sourceWallet);
    if (!validWalletNumber) {
      setFundingError(
        "This wallet cannot be funded. Please select a wallet with a valid wallet number."
      );
      setFundingLoading(false);
      return;
    }

    try {
      // First, initiate the funding transaction
      const initiateRes = await initiateWalletFunding({
        funding_amount: Number(amount),
        funding_currency_code: currency,
        destination_wallet_number_or_uuid: validWalletNumber,
        description: description || `Wallet funding for ${validWalletNumber}`,
      });

      if (initiateRes.status && initiateRes.data) {
        // Then process the funding with transaction pin
        const processRes = await processWalletFunding({
          funding_amount: Number(amount),
          funding_currency_code: currency,
          destination_wallet_number_or_uuid: validWalletNumber,
          description: description || `Wallet funding for ${validWalletNumber}`,
          transaction_pin: pin,
          // Optional redirect URLs - you can customize these
          success_redirect_url: `${window.location.origin}/wallets?status=success`,
          cancelled_redirect_url: `${window.location.origin}/wallets?status=cancelled`,
          failed_redirect_url: `${window.location.origin}/wallets?status=failed`,
        });

        if (processRes.status) {
          setFundingResult(processRes.data);
          setActiveStep(2);
          setSnackbarMsg("Wallet funding initialized successfully!");
          setSnackbarOpen(true);
        } else {
          setFundingError(processRes.message || "Funding processing failed.");
        }
      } else {
        setFundingError(initiateRes.message || "Funding initiation failed.");
      }
    } catch (e) {
      setFundingError(
        e?.response?.data?.message || "Funding failed. Please try again."
      );
    }
    setFundingLoading(false);
  };

  const handleReset = () => {
    setActiveStep(0);
    setAmount("");
    setCurrency("NGN");
    setDescription("");
    setPin("");
    setFundingError("");
    setFundingResult(null);
  };

  const handleGoBack = () => {
    navigate("/wallets");
  };

  if (isLoading)
    return <Preloader fullScreen={true} message="Loading wallets..." />;
  if (error)
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-3 sm:p-4">
        <div className="fintech-card p-6 sm:p-8 text-center max-w-sm sm:max-w-md">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-danger-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <ArrowBackIcon className="text-danger-600 text-xl sm:text-2xl" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
            Failed to Load Wallets
          </h2>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">{error}</p>
          <button
            onClick={handleGoBack}
            className="btn-primary text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
          >
            Go Back
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200/20 to-primary-300/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-indigo-300/20 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/80 backdrop-blur-xl border-b border-white/20 px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <button
              onClick={() => setSidebarOpen && setSidebarOpen(true)}
              className="block xl:hidden text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 rounded-xl p-1.5 sm:p-2 transition-all duration-200"
            >
              <MenuIcon className="text-xl sm:text-2xl" />
            </button>
            <button
              onClick={handleGoBack}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 rounded-xl p-1.5 sm:p-2 transition-all duration-200"
            >
              <ArrowBackIcon className="text-xl sm:text-2xl" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent truncate">
                Add Funds
              </h1>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base truncate">
                Fund your wallet with external payment
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
        <div className="max-w-4xl mx-auto">
          {/* Source Wallet Card */}
          {sourceWallet && (
            <div className="fintech-card p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
                Target Wallet
              </h2>
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center font-bold text-lg sm:text-xl shadow-sm ${
                    getCurrencyCode(sourceWallet) === "NGN"
                      ? "bg-green-100 text-green-700"
                      : getCurrencyCode(sourceWallet) === "USD"
                      ? "bg-blue-100 text-blue-700"
                      : getCurrencyCode(sourceWallet) === "USDT"
                      ? "bg-green-100 text-green-700"
                      : getCurrencyCode(sourceWallet) === "GBP"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {getCurrencyCode(sourceWallet) === "USDT" ? (
                    <SiTether className="text-lg sm:text-xl" />
                  ) : (
                    getFlag(getCurrencyCode(sourceWallet))
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm sm:text-base font-semibold text-gray-900">
                    {sourceWallet.ownership_label ||
                      `${getCurrencyCode(sourceWallet)} Wallet`}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {sourceWallet.wallet_number}
                  </div>
                  <div className="text-sm sm:text-base font-bold text-gray-900 mt-1">
                    {getSymbol(getCurrencyCode(sourceWallet))}
                    {parseFloat(sourceWallet.balance || 0).toLocaleString(
                      undefined,
                      { minimumFractionDigits: 2 }
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Funding Steps */}
          <div className="fintech-card p-4 sm:p-6">
            <Stepper activeStep={activeStep} className="mb-6 sm:mb-8">
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            <div className="min-h-[400px]">
              {activeStep === 0 && (
                <div className="space-y-4">
                  <Typography variant="h6" className="mb-4">
                    Enter Funding Details
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    sx={{ mb: 3 }}
                  >
                    {supportedCurrencies.map((currencyCode) => (
                      <MenuItem key={currencyCode} value={currencyCode}>
                        <div className="flex items-center gap-2">
                          {/* {currencyCode === "USDT" ? (
                            <SiTether className="text-lg" />
                          ) : (
                            getFlag(currencyCode)
                          )} */}
                          {currencyCode}
                        </div>
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getSymbol(currency)}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ mb: 3 }}
                    multiline
                    rows={3}
                  />
                </div>
              )}

              {activeStep === 1 && (
                <div className="space-y-4">
                  <Typography variant="h6" className="mb-4">
                    Confirm Funding
                  </Typography>

                  <Paper
                    elevation={1}
                    sx={{ p: 3, mb: 3, background: "#f8fafc" }}
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-semibold">
                          {getSymbol(currency)}
                          {parseFloat(amount || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Currency:</span>
                        <span className="font-semibold">{currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">To Wallet:</span>
                        <span className="font-semibold">
                          {sourceWallet?.wallet_number}
                        </span>
                      </div>
                      {description && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Description:</span>
                          <span className="font-semibold">{description}</span>
                        </div>
                      )}
                    </div>
                  </Paper>

                  <TextField
                    fullWidth
                    label="Transaction PIN"
                    type={showPin ? "text" : "password"}
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    sx={{ mb: 3 }}
                    inputProps={{ maxLength: 6 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPin(!showPin)}
                            edge="end"
                          >
                            {showPin ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              )}

              {activeStep === 2 && fundingResult && (
                <div className="space-y-4">
                  <Typography
                    variant="h6"
                    color="success.main"
                    className="mb-4"
                  >
                    Funding Initialized!
                  </Typography>

                  <Paper
                    elevation={1}
                    sx={{ p: 3, mb: 3, background: "#e8f5e8" }}
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-semibold">
                          {getSymbol(fundingResult.currency_code || currency)}
                          {fundingResult.amount_to_fund || amount}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Wallet:</span>
                        <span className="font-semibold">
                          {fundingResult.wallet_number ||
                            sourceWallet?.wallet_number}
                        </span>
                      </div>
                      {fundingResult.initial_balance && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Current Balance:
                          </span>
                          <span className="font-semibold">
                            {getSymbol(fundingResult.currency_code || currency)}
                            {fundingResult.initial_balance}
                          </span>
                        </div>
                      )}
                      {fundingResult.expected_balance_after_funding && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Expected Balance:
                          </span>
                          <span className="font-semibold">
                            {getSymbol(fundingResult.currency_code || currency)}
                            {fundingResult.expected_balance_after_funding}
                          </span>
                        </div>
                      )}
                      {fundingResult.platform_fee && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Platform Fee:</span>
                          <span className="font-semibold">
                            {getSymbol(fundingResult.currency_code || currency)}
                            {fundingResult.platform_fee}
                          </span>
                        </div>
                      )}
                      {fundingResult.total_payable && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Payable:</span>
                          <span className="font-semibold">
                            {getSymbol(fundingResult.currency_code || currency)}
                            {fundingResult.total_payable}
                          </span>
                        </div>
                      )}
                      {fundingResult.payment_reference && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Reference:</span>
                          <span className="font-semibold">
                            {fundingResult.payment_reference}
                          </span>
                        </div>
                      )}
                    </div>
                  </Paper>

                  {fundingResult.funding_url && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mb: 2 }}
                      onClick={() =>
                        window.open(fundingResult.funding_url, "_blank")
                      }
                    >
                      Complete Payment
                    </Button>
                  )}

                  {fundingResult.pre_funding_instruction && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        background: "#fff3cd",
                        border: "1px solid #ffeaa7",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="warning.main"
                        className="mb-2"
                      >
                        Instructions:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {fundingResult.pre_funding_instruction}
                      </Typography>
                    </Paper>
                  )}
                </div>
              )}

              {fundingError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {fundingError}
                </Alert>
              )}
            </div>

            {/* Step Actions */}
            <div className="flex justify-between mt-6 sm:mt-8">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <div>
                {activeStep === steps.length - 1 ? (
                  <Button variant="contained" onClick={handleReset}>
                    Fund Another Wallet
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={activeStep === 1 ? handleFund : handleNext}
                    disabled={
                      fundingLoading ||
                      (activeStep === 0 && (!amount || Number(amount) <= 0)) ||
                      (activeStep === 1 && !pin)
                    }
                  >
                    {fundingLoading ? (
                      <CircularProgress size={20} />
                    ) : activeStep === 1 ? (
                      "Fund Wallet"
                    ) : (
                      "Next"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}
